import { getDevicePref, getUserPref, setUserPref, spErrorTags, spLogTags } from "@eventr/common";
import { useMobileDateRangePickerStore } from "@eventr/common-storybook";
import { deviceDetect } from "react-device-detect";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";
import i18n from "../../i18n/i18next";
import { useOverlayStore } from "../overlayState/overlay.store";
import { getDevicePrefName } from "../../utils";

type TTimeFormats = "HH:mm" | "h:mma";

export enum availableLang {
  en_GB = "en-GB",
  en_US = "en-US",
  bg_BG = "bg-BG",
  bs_BA = "bs-BA",
  cs_CZ = "cs-CZ",
  da_DK = "da-DK",
  de_DE = "de-DE",
  el = "el",
  es_ES = "es-ES",
  et_EE = "et-EE",
  fi_FI = "fi-FI",
  fr = "fr",
  hi_IN = "hi-IN",
  hu = "hu",
  id_ID = "id-ID",
  it = "it",
  ja = "ja",
  ko = "ko",
  lt_LT = "lt-LT",
  lv_LV = "lv-LV",
  ms_MY = "ms-MY",
  nl = "nl",
  no_NO = "no-NO",
  pl = "pl",
  pt_BR = "pt-BR",
  pt = "pt",
  ro_RO = "ro-RO",
  ru = "ru",
  sk_SK = "sk-SK",
  sl_SI = "sl-SI",
  so_SO = "so-SO",
  sq_AL = "sq-AL",
  sr_RS = "sr-RS",
  su_ID = "su-ID",
  sv_SE = "sv-SE",
  sw_KE = "sw-KE",
  th_TH = "th-TH",
  fil_PH = "fil-PH",
  tr = "tr",
  uk_UA = "uk-UA",
  uz_UZ = "uz-UZ",
  vi_VN = "vi-VN",
  zh_CN = "zh-CN",
  zh_TW = "zh-TW",
}

type TLanguageState = {
  selectedLanguage: availableLang;
  setLanguage: (lang: availableLang) => void;
  getLanguage: () => availableLang;
  getLanguageISO639Code: () => string;

  /**
   * The language being switched to, or null when nothing is in flight. Spans i18next's
   * chunk fetch and the remount that follows. Drives the loading state and the navigation
   * hold.
   */
  pendingLanguage: availableLang | null;
  setPendingLanguage: (lang: availableLang | null) => void;

  timeFormat: TTimeFormats;
  setTimeFormat: (format: TTimeFormats) => void;
};

/**
 * Bases 12 or 24 hour clock on language... i hate this but no better way other than allowing the user to set their own preference.
 */
function sortTimeFormat(): TTimeFormats {
  const userPrefTimeFormat: TTimeFormats = getUserPref("account", "timeFormat");

  if (userPrefTimeFormat) {
    return userPrefTimeFormat;
  }

  /// Defualt from lang???
  try {
    const date = new Date();
    const dateString = date.toLocaleTimeString(window.navigator.language, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeStyle: "short",
    });

    if (dateString.match(/am|pm/i) || date.toString().match(/am|pm/i)) {
      //12 hour clock
      return "h:mma";
    } else {
      //24 hour clock
      return "HH:mm";
    }
  } catch {
    return "HH:mm";
  }
}

export const useLanguageContext = create<TLanguageState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => {
        const deviceInfo = getDevicePrefName(deviceDetect);
        const currentLanguage =
          localStorage.getItem("i18nextLng") ||
          getDevicePref("lang", deviceInfo, availableLang.en_GB) ||
          availableLang.en_GB;

        return {
          selectedLanguage: currentLanguage,
          setLanguage: (lang: availableLang) => {
            localStorage.setItem("selectedLanguage", lang);
            set({ selectedLanguage: lang });
          },
          pendingLanguage: null,
          setPendingLanguage: (lang: availableLang | null) => {
            if (get().pendingLanguage === lang) return;
            spLogTags(lang ? `language change started: ${lang}` : "language change settled", ["i18n"]);
            set({ pendingLanguage: lang });
          },

          getLanguage: () => get().selectedLanguage,
          getLanguageISO639Code: () => {
            switch (get().selectedLanguage) {
              case availableLang.en_GB:
                return "en-GB";
              case availableLang.en_US:
                return "en-US";
              case availableLang.bg_BG:
                return "bg";
              case availableLang.bs_BA:
                return "bs";
              case availableLang.cs_CZ:
                return "cs";
              case availableLang.da_DK:
                return "da";
              case availableLang.es_ES:
                return "es";
              case availableLang.de_DE:
                return "de";
              case availableLang.el:
                return "el";
              case availableLang.et_EE:
                return "et";
              case availableLang.fi_FI:
                return "fi";
              case availableLang.fr:
                return "fr";
              case availableLang.hi_IN:
                return "hi";
              case availableLang.hu:
                return "hu";
              case availableLang.id_ID:
                return "id";
              case availableLang.it:
                return "it";
              case availableLang.ja:
                return "ja";
              case availableLang.ko:
                return "ko";
              case availableLang.lt_LT:
                return "lt";
              case availableLang.lv_LV:
                return "lv";
              case availableLang.ms_MY:
                return "ms";
              case availableLang.nl:
                return "nl";
              case availableLang.no_NO:
                return "no";
              case availableLang.pl:
                return "pl";
              case availableLang.pt_BR:
                return "pt-BR";
              case availableLang.pt:
                return "pt";
              case availableLang.ro_RO:
                return "ro";
              case availableLang.ru:
                return "ru";
              case availableLang.sk_SK:
                return "sk";
              case availableLang.sl_SI:
                return "sl";
              case availableLang.so_SO:
                return "so";
              case availableLang.sq_AL:
                return "sq";
              case availableLang.sr_RS:
                return "sr";
              case availableLang.su_ID:
                return "su";
              case availableLang.sv_SE:
                return "sv";
              case availableLang.sw_KE:
                return "sw";
              case availableLang.th_TH:
                return "th";
              case availableLang.fil_PH:
                return "fil";
              case availableLang.tr:
                return "tr";
              case availableLang.uk_UA:
                return "uk";
              case availableLang.uz_UZ:
                return "uz";
              case availableLang.vi_VN:
                return "vi";
              case availableLang.zh_CN:
              case availableLang.zh_TW:
                return "zh";
              default:
                return "en-GB";
            }
          },

          timeFormat: sortTimeFormat(),
          setTimeFormat: (format: TTimeFormats) => {
            setUserPref("account", "timeFormat", format);
            set({ timeFormat: format });
          },
        };
      },
      { name: "language-context", enabled: DEVTOOLS_ENABLED },
    ),
  ),
);

/**
 * i18next owns the active language; this store mirrors it, and is what the app subscribes
 * to. i18n.t is a plain call into a singleton and gives React nothing to re-render on.
 */
/**
 * languageChanging fires when the switch is asked for; languageChanged only after the
 * locale's chunk has loaded. The wait is raised on the former so it covers the fetch.
 * Skipped before init, which resolves the stored language through the same path.
 */
i18n.on("languageChanging", (lng) => {
  const { selectedLanguage, setPendingLanguage } = useLanguageContext.getState();
  if (!i18n.isInitialized || !lng || lng === selectedLanguage) return;
  setPendingLanguage(lng as availableLang);
});

i18n.on("languageChanged", (lng) => {
  const { selectedLanguage, setLanguage } = useLanguageContext.getState();
  if (!lng || lng === selectedLanguage) return;
  setLanguage(lng as availableLang);
});

let changeWatchdog: ReturnType<typeof setTimeout> | undefined;

/**
 * Raises the app's own Backdrop (OverlayWrapper), which renders above the remount gate and
 * so survives the rebuild it covers.
 *
 * While the wait is held the app is blocked: overlay up, navigation refused. The watchdog
 * releases it regardless, so a release that never arrives cannot strand the app.
 */
useLanguageContext.subscribe(
  (state) => state.pendingLanguage !== null,
  (changing) => {
    useOverlayStore.getState().setLoading(changing);

    if (changing) {
      clearTimeout(changeWatchdog);
      changeWatchdog = setTimeout(() => {
        spErrorTags("language change never settled, releasing", ["i18n"]);
        useLanguageContext.getState().setPendingLanguage(null);
      }, 10_000);
    } else {
      clearTimeout(changeWatchdog);
      changeWatchdog = undefined;
    }
  },
);

useLanguageContext.subscribe(
  (state) => state.selectedLanguage,
  (state) => {
    if (state) {
      useMobileDateRangePickerStore.setState({ monthsToShow: [] });
      useMobileDateRangePickerStore
        .getState()
        .initializeMonthsToShow(useLanguageContext.getState().getLanguageISO639Code());
    }
  },
  { fireImmediately: true },
);
